import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { $generateNodesFromDOM } from "@lexical/html";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { $getRoot } from "lexical";
import { Suspense } from "react";

// Common components
import AppBar from "../../../common/AppBar";
import Loader from "../../../common/Loader";
import Snackbar from "../../../common/Snackbar";
import { SaveModal } from "../../../common/Modals";
import {
  VersionHistoryPopup,
  getVersionHistoryAuditPreviewPath,
} from "../../../common/VersionHistoryPopup";
import { TextField, TextArea } from "../../../common/FormFields";
import InputWithPen from "../../../common/Inputs/InputWithPen";
import CategoryPopup from "../../Category/CategoryPopup";

// Rich Text Editor components
import Editor from "../../../common/RichTextEditor/Editor/Editor";
import { SharedHistoryContext } from "../../../common/RichTextEditor/LexicalEditor/context/SharedHistoryContext";
import { TableContext } from "../../../common/RichTextEditor/LexicalEditor/plugins/TablePlugin";
import { ToolbarContext } from "../../../common/RichTextEditor/LexicalEditor/context/ToolbarContext";
import PlaygroundNodes from "../../../common/RichTextEditor/LexicalEditor/nodes/PlaygroundNodes";
import PlaygroundEditorTheme from "../../../common/RichTextEditor/LexicalEditor/themes/PlaygroundEditorTheme";
import { buildImportMap, cleanBaselineHtml } from "../../../util/lexical";
import { useSettings } from "../../../common/RichTextEditor/LexicalEditor/context/SettingsContext";

// Redux actions
import { createRichText, updateRichText } from "../../../redux/stepSlice";
import { openPreview } from "../../../redux/previewSlice";
import { getAllCategories } from "../../../redux/categorySlice";

// Utilities
import { getAuditTrailData } from "../../../util/getAuditTrailData";
import { useUnsavedChangesBlocker } from "../../../hooks/useUnsavedChangesBlocker";
import { setTaskReference } from "../../Tasks/Uitlities";
import { pingTask } from "../../../util/apiService";

// Custom hooks
import useRichTextData from "../hooks/useRichTextData";
import useRichTextPersistence from "../hooks/useRichTextPersistence";

// Styles
import "../RichText.css";
import { buildPermissionContext } from "../../../util/permissions";
import { useVersionHistoryDataFromNodes } from "../../../common/VersionHistoryPopup/useVersionHistoryDataFromNodes.js";

/** Global "Rich Text N" index — same traversal as ViewChildNode / ChildrenRenderer (`rich-text`). */
function computeGlobalMediaOrdinalForRichText({
  allRevisionNodes,
  section_id_ref,
  step_id_ref,
  child_node_id,
}) {
  const nodeType = "rich-text";
  if (
    !child_node_id ||
    !Array.isArray(allRevisionNodes) ||
    !section_id_ref ||
    !step_id_ref
  ) {
    return null;
  }

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  const sortedSections = [...allRevisionNodes].sort((a, b) => {
    const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
    const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
    const orderCompare = compareSiblingOrders(orderA, orderB);
    if (orderCompare !== 0) return orderCompare;
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  let counter = 0;
  const matchesId = (c) =>
    c.tree_node_id === child_node_id ||
    c.detail?.canvas_tree_node_id === child_node_id ||
    c.detail?.attachment_tree_node_id === child_node_id ||
    c.detail?.rich_text_node_id === child_node_id ||
    c.detail?.element_id === child_node_id ||
    c.element_id === child_node_id;

  for (let i = 0; i < sortedSections.length; i++) {
    const section = sortedSections[i];
    for (let j = 0; j < (section.steps?.length || 0); j++) {
      const step = section.steps[j];
      const stepChildren =
        step.children?.filter((c) => c.type === nodeType) || [];

      const currentStepChild = stepChildren.find(matchesId);
      if (currentStepChild) {
        const sortedStepChildren = [...stepChildren].sort((a, b) => {
          const orderA = a.sibling_order || 0;
          const orderB = b.sibling_order || 0;
          return orderA - orderB;
        });
        const currentChildIndex = sortedStepChildren.findIndex(matchesId);
        counter += currentChildIndex;
        return counter + 1;
      }
      counter += stepChildren.length;

      for (let k = 0; k < (step.substeps?.length || 0); k++) {
        const substep = step.substeps[k];
        const substepChildren =
          substep.children?.filter((c) => c.type === nodeType) || [];
        const currentSubstepChild = substepChildren.find(matchesId);
        if (currentSubstepChild) {
          const sortedSubstepChildren = [...substepChildren].sort((a, b) => {
            const orderA = a.sibling_order || 0;
            const orderB = b.sibling_order || 0;
            return orderA - orderB;
          });
          const currentChildIndex =
            sortedSubstepChildren.findIndex(matchesId);
          counter += currentChildIndex;
          return counter + 1;
        }
        counter += substepChildren.length;
      }
    }
  }
  return counter + 1;
}

/**
 * RichTextForm - Optimized rich text form component
 *
 * Key improvements:
 * - Custom hooks for data management
 * - Reduced location state dependencies
 * - Better error handling
 * - Cleaner state management
 * - Improved persistence strategy
 */
const RichTextForm = ({
  // Core props
  mode,

  // URL parameters
  partnumber,
  revision,
  partId,
  revisionId,
  section_id_ref,
  step_id_ref,
  substep_id_ref,
  node_id_ref,
  rich_text_id_ref,

  // Version history
  fromVersionHistory,
  versionHistoryData,

  // Optional state data
  status,
  description,
  type,
  attachmentCount,
  richCount,
  canvasCount,
  videoCount,
  formCount,
  stepNumber,
  data,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;

  // Scroll to top when opening edit/add Rich Text screen - runs on mount
  useEffect(() => {
    const prevRestoration = window.history?.scrollRestoration;
    if (window.history?.scrollRestoration !== undefined) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    return () => {
      if (window.history?.scrollRestoration !== undefined && prevRestoration) {
        window.history.scrollRestoration = prevRestoration;
      }
    };
  }, []);

  // Ping API call every minute to keep lock alive
  useEffect(() => {
    if (!revisionId || !authUser) return;

    const pingInterval = setInterval(async () => {
      try {
        await pingTask({
          task_id: revisionId,
          user_id: authUser.id,
        });
      } catch (error) {
        // Ping failed - silent
      }
    }, 60000); // 60 seconds = 1 minute

    return () => {
      clearInterval(pingInterval);
    };
  }, [revisionId, authUser]);

  // Use custom hooks for data management
  const {
    loading,
    richTextData,
    versionId,
    procedureId,
    lastSavedText,
    pageTitle,
    breadcrumbText,
    siblingOrder,
    allNodesData,
    sectionId,
    stepId,
    substepId,
    currentStatus,
    item_code_type
  } = useRichTextData({
    mode,
    id: rich_text_id_ref,
    partnumber,
    revision,
    partId,
    revisionId,
    section_id_ref,
    step_id_ref,
    substep_id_ref,
    node_id_ref,
  });
  const {
    formData,
    setFormData,
    setFormDataSilent,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    saveForPreview,
    clearStoredData,
    dataLoaded,
  } = useRichTextPersistence({
    mode,
    id: rich_text_id_ref,
    richTextData,
  });

  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [comment, setComment] = useState("");
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const richRouteChildId =
    richTextData?.tree_node_id || rich_text_id_ref || null;

  const richTextVersionHistoryMatchIds = useMemo(() => {
    const fromAttachments = Array.isArray(richTextData?.detail?.attachments)
      ? richTextData.detail.attachments.flatMap((a) =>
          [a?.attachment_tree_node_ref, a?.attachment_id].filter(Boolean),
        )
      : [];
    return [
      rich_text_id_ref,
      richTextData?.tree_node_id,
      richTextData?.detail?.procedure_tree_ref,
      richTextData?.attachment_id_ref,
      richTextData?.attachment_tree_node_id,
      richTextData?.detail?.attachment_id,
      richTextData?.detail?.attachment_tree_node_id,
      richTextData?.detail?.video_tree_node_id,
      richTextData?.detail?.rich_text_node_id,
      richTextData?.detail?.rich_text_tree_node_id,
      richTextData?.detail?.canvas_tree_node_id,
      richTextData?.detail?.canva_tree_node_id,
      richTextData?.detail?.form_tree_node_id,
      richTextData?.detail?.element_id,
      ...fromAttachments,
    ].filter(Boolean);
  }, [
    rich_text_id_ref,
    richTextData?.tree_node_id,
    richTextData?.detail?.procedure_tree_ref,
    richTextData?.attachment_id_ref,
    richTextData?.attachment_tree_node_id,
    richTextData?.detail?.attachment_id,
    richTextData?.detail?.attachment_tree_node_id,
    richTextData?.detail?.video_tree_node_id,
    richTextData?.detail?.rich_text_node_id,
    richTextData?.detail?.rich_text_tree_node_id,
    richTextData?.detail?.canvas_tree_node_id,
    richTextData?.detail?.canva_tree_node_id,
    richTextData?.detail?.form_tree_node_id,
    richTextData?.detail?.element_id,
    richTextData?.detail?.attachments,
  ]);

  const richTextNodeHistoryLeafOrdinal = useMemo(() => {
    const fromTitle =
      typeof pageTitle === "string"
        ? pageTitle.match(/Rich\s+Text\s+(\d+)/i)
        : null;
    if (fromTitle) {
      const n = parseInt(fromTitle[1], 10);
      if (Number.isFinite(n) && n >= 1) return n;
    }
    const fromTraversal = computeGlobalMediaOrdinalForRichText({
      allRevisionNodes: allNodesData,
      section_id_ref,
      step_id_ref,
      child_node_id: richRouteChildId,
    });
    return fromTraversal != null && Number.isFinite(Number(fromTraversal))
      ? Number(fromTraversal)
      : undefined;
  }, [
    pageTitle,
    allNodesData,
    section_id_ref,
    step_id_ref,
    richRouteChildId,
  ]);

  const {
    mergedVersionHistories,
    allVersionHistories,
    versionHistoryTableLoading,
  } = useVersionHistoryDataFromNodes({
    isOpen: showVersionHistory && mode === "edit",
    revisionId,
    partId,
    allRevisionNodes: allNodesData,
    fallbackVersionId:
      richTextData?.detail?.version_id_ref ||
      richTextData?.version_id_ref ||
      null,
    nodesReady:
      mode === "edit" &&
      !loading &&
      Array.isArray(allNodesData) &&
      allNodesData.length > 0,
  });

  const versionHistories = mergedVersionHistories;
  const versionHistoryLoading = versionHistoryTableLoading;

  const {
    settings: { isCollab, emptyEditor },
  } = useSettings();

  // Undo-based dirty tracking for the Lexical short-description editor.
  // Must be declared BEFORE useUnsavedChangesBlocker which reads it.
  const [isShortDescriptionDirty, setIsShortDescriptionDirty] = useState(false);
  const handleShortDescriptionDirtyChange = useCallback(
    (dirty) => setIsShortDescriptionDirty(dirty),
    []
  );

  // Callback to run when user confirms leaving.
  // Must call history.back() to navigate (blocker delegates to us when this callback exists).
  // Reload fixes React Router location sync issue.
  const onConfirmNavigate = useCallback(() => {
    window.history.back();
    setTimeout(() => window.location.reload(), 50);
  }, []);

  // Navigation protection
  useUnsavedChangesBlocker(
    (hasUnsavedChanges || isShortDescriptionDirty) && !isLoading,
    "You have unsaved changes. Are you sure you want to leave?",
    onConfirmNavigate
  );

  // Scroll to top again when form loads (after Loader disappears) - overrides any focus/editor-induced scroll
  useEffect(() => {
    if (loading || (mode === "edit" && !dataLoaded)) return;
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [loading, dataLoaded, mode]);

  // Load categories when form mounts so category popup has data (match RichTextFormContainer)
  useEffect(() => {
    dispatch(getAllCategories({ limit: 100, offset: 0 }));
  }, [dispatch]);

  // Load categories when category popup opens so list is fresh
  useEffect(() => {
    if (categoryModalOpen) {
      dispatch(getAllCategories({ limit: 100, offset: 0 }));
    }
  }, [categoryModalOpen, dispatch]);

  // Editor state management
  const initialContentRef = useRef(null);
  const isInitializedRef = useRef(false);
  const isInitializingRef = useRef(true); // Track if we're still in initialization phase

  // Refs for sticky elements
  const appBarRef = useRef(null);
  const toolbarRef = useRef(null);

  // Clean up HTML to remove empty paragraphs and excessive br tags
  const cleanHtmlContent = (htmlString) => {
    if (!htmlString) return htmlString;

    try {
      // Remove the editor-shell wrapper if present
      let cleaned = htmlString
        .replace(/<div class="editor-shell">/g, "")
        .replace(/<\/div>\s*$/, "");

      const parser = new DOMParser();
      const wrappedHtml = `<div>${cleaned}</div>`;
      const dom = parser.parseFromString(wrappedHtml, "text/html");
      const container = dom.body.firstElementChild;

      if (!container) return htmlString;

      // Helper to check if a paragraph is empty (only whitespace/br)
      const isEmptyParagraph = (p) => {
        const textContent = p.textContent?.trim() || "";
        const innerHTML = p.innerHTML.trim();
        // Check for empty or only br tags (with various formats)
        const hasOnlyBr =
          /^<br\s*\/?>$/i.test(innerHTML) ||
          /^<br\s*\/?>\s*$/i.test(innerHTML) ||
          innerHTML === "" ||
          innerHTML === "&nbsp;" ||
          /^(&nbsp;|\s)*$/i.test(innerHTML);
        return (
          textContent === "" &&
          (hasOnlyBr ||
            (p.children.length === 0 &&
              !p.querySelector(
                "img, figure, [data-lexical-equation], table, hr, details"
              )))
        );
      };

      // Helper to check if an element is a block-level element that needs spacing
      const isBlockElement = (el) => {
        if (!el) return false;
        // Check if paragraph contains block elements
        if (el.tagName === "P") {
          return (
            el.querySelector(
              "img, figure, [data-lexical-equation], table, hr, details, .inline-editor-image"
            ) !== null
          );
        }
        // Check direct block elements
        return (
          ["DIV", "HR", "FIGURE", "TABLE", "DETAILS"].includes(el.tagName) ||
          el.querySelector(
            "img, figure, [data-lexical-equation], table, hr, details, .inline-editor-image"
          ) !== null
        );
      };

      // Helper to check if an element contains a floated image
      const hasFloatedImage = (el) => {
        if (!el) return false;
        const floatedImage = el.querySelector(
          ".inline-editor-image.position-left, .inline-editor-image.position-right"
        );
        return floatedImage !== null;
      };

      // Get all direct children (paragraphs and other elements)
      const allElements = Array.from(container.children);

      // Process elements to remove excessive empty paragraphs while preserving spacing between blocks
      const elementsToRemove = [];
      let consecutiveEmptyCount = 0;

      allElements.forEach((el, index) => {
        if (el.tagName === "P" && isEmptyParagraph(el)) {
          consecutiveEmptyCount++;
          const prevElement = allElements[index - 1];
          const nextElement = allElements[index + 1];
          const prevIsBlock = isBlockElement(prevElement);
          const nextIsBlock = isBlockElement(nextElement);
          const prevHasFloated = hasFloatedImage(prevElement);

          // Special handling for floated images - need more spacing/clearance
          if (prevHasFloated) {
            // Keep at least 2-3 empty paragraphs after floated images for proper clearance
            if (consecutiveEmptyCount > 3) {
              elementsToRemove.push(el);
            }
            // Don't remove if it's one of the first 3 after a floated image
          }
          // If this empty paragraph is between two block elements
          else if (prevIsBlock && nextIsBlock) {
            // Keep the first one for spacing, remove the rest
            if (consecutiveEmptyCount > 1) {
              elementsToRemove.push(el);
            } else {
              // Keep this first empty paragraph between blocks
              consecutiveEmptyCount = 0; // Reset to allow one more if needed
            }
          } else if (prevIsBlock && !nextIsBlock) {
            // After a block but before non-block - keep first, remove rest
            if (consecutiveEmptyCount > 1) {
              elementsToRemove.push(el);
            } else {
              consecutiveEmptyCount = 0;
            }
          } else {
            // Between regular text paragraphs - preserve intentional spacing
            // Keep the first empty paragraph (user pressed Enter for spacing),
            // but remove excessive consecutive empty paragraphs (more than 1)
            if (consecutiveEmptyCount > 1) {
              elementsToRemove.push(el);
            }
          }
        } else {
          // Reset counter when we hit a non-empty element
          consecutiveEmptyCount = 0;
        }
      });

      // Remove identified empty paragraphs
      elementsToRemove.forEach((el) => el.remove());

      // Re-query elements after removal
      const remainingElements = Array.from(container.children);

      // Add clearfix after floated images to ensure proper layout
      remainingElements.forEach((el, index) => {
        if (hasFloatedImage(el)) {
          // Find the next non-empty block element (like equation)
          let nextBlockIndex = index + 1;
          let nextBlockElement = null;

          while (nextBlockIndex < remainingElements.length) {
            const candidate = remainingElements[nextBlockIndex];
            if (isBlockElement(candidate) && !isEmptyParagraph(candidate)) {
              nextBlockElement = candidate;
              break;
            }
            nextBlockIndex++;
          }

          if (nextBlockElement && nextBlockElement.tagName === "P") {
            // Add clear: both style to ensure it appears below floated image
            const currentStyle = nextBlockElement.getAttribute("style") || "";
            if (!currentStyle.includes("clear")) {
              nextBlockElement.setAttribute(
                "style",
                `${currentStyle ? currentStyle + "; " : ""}clear: both;`
              );
            }
          } else if (nextBlockElement && nextBlockElement.tagName === "DIV") {
            // For div elements, add clearfix class or style
            const currentStyle = nextBlockElement.getAttribute("style") || "";
            if (!currentStyle.includes("clear")) {
              nextBlockElement.setAttribute(
                "style",
                `${currentStyle ? currentStyle + "; " : ""}clear: both;`
              );
            }
          }
          // Note: We don't add empty paragraphs here anymore - let the user control spacing
        }
      });

      // Remove any remaining standalone <br> tags
      const finalElements = Array.from(container.children);
      finalElements.forEach((el) => {
        if (el.tagName === "BR") {
          el.remove();
        }
      });

      // Clean up excessive <br> tags within remaining paragraphs
      const remainingParagraphs = container.querySelectorAll("p");
      remainingParagraphs.forEach((p) => {
        let innerHTML = p.innerHTML;
        const textOnly = p.textContent?.trim() || "";

        // If this paragraph has only <br> (empty spacer paragraph), keep it as-is
        // so that intentional Enter spacing is preserved
        if (textOnly === "" && /^(<br\s*\/?>|\s|&nbsp;)*$/i.test(innerHTML)) {
          // Normalize to a single <br> to ensure it renders as a visible empty line
          p.innerHTML = "<br>";
          return;
        }

        // For paragraphs with actual text content, clean up excessive <br> tags
        // Remove all standalone <br> tags at start/end
        innerHTML = innerHTML.replace(/^(<br\s*\/?>)+/i, "");
        innerHTML = innerHTML.replace(/(<br\s*\/?>)+$/i, "");
        // Replace 3+ consecutive <br> tags with single <br> (keep some spacing)
        innerHTML = innerHTML.replace(/(<br\s*\/?>){3,}/gi, "<br>");
        p.innerHTML = innerHTML;
      });

      // Final pass: remove paragraphs that became completely empty after cleaning
      // Only remove if they are leading/trailing (no content before/after) — preserve
      // intentional spacing between content paragraphs
      const finalParagraphs = Array.from(container.querySelectorAll("p"));
      finalParagraphs.forEach((p) => {
        const textContent = p.textContent?.trim() || "";
        const innerHTML = p.innerHTML.trim();
        if (
          textContent === "" &&
          (innerHTML === "" ||
            innerHTML === "<br>" ||
            /^<br\s*\/?>$/i.test(innerHTML))
        ) {
          const prevSibling = p.previousElementSibling;
          const nextSibling = p.nextElementSibling;

          // Remove only if this is a leading empty paragraph (nothing before)
          // or a trailing empty paragraph (nothing after)
          const isLeading = !prevSibling;
          const isTrailing = !nextSibling;

          if (isLeading || isTrailing) {
            p.remove();
          }
          // Otherwise keep it — the user intentionally pressed Enter for spacing
        }
      });

      // Remove PlaygroundEditorTheme__paragraph class from all paragraphs
      const allParagraphs = container.querySelectorAll("p");
      allParagraphs.forEach((p) => {
        p.classList.remove("PlaygroundEditorTheme__paragraph");
      });

      const cleanedHtml = container.innerHTML;

      // Also ensure clearance after floated images
      const finalHtml = ensureClearanceAfterFloatedImages(cleanedHtml);
      return finalHtml;
    } catch (error) {
      return htmlString;
    }
  };

  // Ensure proper clearance for elements after floated images
  const ensureClearanceAfterFloatedImages = (htmlString) => {
    if (!htmlString) return htmlString;

    try {
      const parser = new DOMParser();
      // Check if content is wrapped in editor-shell
      const hasEditorShell = htmlString.includes('class="editor-shell"');

      const wrappedHtml = `<div>${htmlString}</div>`;
      const dom = parser.parseFromString(wrappedHtml, "text/html");
      let container = dom.body.firstElementChild;

      if (!container) {
        return htmlString;
      }

      // If we have editor-shell wrapper, get its children instead
      let editorShell = container.querySelector(".editor-shell");
      if (editorShell) {
        container = editorShell;
      } else if (
        container.classList &&
        container.classList.contains("editor-shell")
      ) {
        // Container itself is editor-shell
        editorShell = container;
      }

      const allElements = Array.from(container.children);

      let floatedImageCount = 0;
      let clearedElementsCount = 0;

      allElements.forEach((el, index) => {
        // Check if this element contains a floated image
        const floatedImage = el.querySelector(
          ".inline-editor-image.position-left, .inline-editor-image.position-right"
        );

        if (floatedImage) {
          floatedImageCount++;

          // Check if there's already a clearfix after this element
          const nextSibling = el.nextElementSibling;
          const hasClearfix =
            nextSibling &&
            nextSibling.classList &&
            nextSibling.classList.contains("clearfix");

          // If no clearfix exists, add one after the floated image paragraph
          if (!hasClearfix) {
            const clearfixDiv = dom.createElement("div");
            clearfixDiv.className = "clearfix";
            clearfixDiv.setAttribute(
              "style",
              "clear: both !important; display: block !important; width: 100% !important; height: 0 !important; margin: 0 !important; padding: 0 !important;"
            );
            if (el.nextSibling) {
              el.parentNode.insertBefore(clearfixDiv, el.nextSibling);
            } else {
              el.parentNode.appendChild(clearfixDiv);
            }
          }

          // Find the next non-empty block element
          for (let i = index + 1; i < allElements.length; i++) {
            const nextEl = allElements[i];
            const textContent = nextEl.textContent?.trim() || "";
            const equationSpan = nextEl.querySelector(
              "[data-lexical-equation]"
            );
            const hasContent =
              textContent.length > 0 ||
              nextEl.querySelector(
                "img, [data-lexical-equation], table, hr, details"
              ) !== null;

            // Handle both P tags and DIV wrappers that contain equations
            if (
              hasContent &&
              (nextEl.tagName === "P" ||
                (nextEl.tagName === "DIV" && equationSpan))
            ) {
              clearedElementsCount++;

              if (
                nextEl.tagName === "DIV" &&
                nextEl.classList.contains("equation-clearfix-wrapper")
              ) {
                // Already wrapped, just ensure it has the right styles
                const currentDivStyle = nextEl.getAttribute("style") || "";
                let newDivStyle = currentDivStyle;
                if (!newDivStyle.includes("clear: both")) {
                  newDivStyle = `${newDivStyle ? newDivStyle + "; " : ""
                    }clear: both !important;`;
                }
                if (!newDivStyle.includes("display: block")) {
                  newDivStyle = `${newDivStyle} display: block !important;`;
                }
                if (!newDivStyle.includes("width: 100%")) {
                  newDivStyle = `${newDivStyle} width: 100% !important;`;
                }
                if (newDivStyle !== currentDivStyle) {
                  nextEl.setAttribute("style", newDivStyle);
                }

                // Also ensure the paragraph inside has styles
                const paraInside = nextEl.querySelector("p");
                if (paraInside) {
                  const currentParaStyle =
                    paraInside.getAttribute("style") || "";
                  let newParaStyle = currentParaStyle;
                  if (!newParaStyle.includes("clear: both")) {
                    newParaStyle = `${newParaStyle ? newParaStyle + "; " : ""
                      }clear: both !important;`;
                  }
                  if (!newParaStyle.includes("display: block")) {
                    newParaStyle = `${newParaStyle} display: block !important;`;
                  }
                  if (!newParaStyle.includes("width: 100%")) {
                    newParaStyle = `${newParaStyle} width: 100% !important;`;
                  }
                  if (newParaStyle !== currentParaStyle) {
                    paraInside.setAttribute("style", newParaStyle);
                  }
                }
              } else if (nextEl.tagName === "P") {
                // Don't wrap - Lexical converts DIVs to paragraphs anyway
                // Instead, ensure the paragraph has very strong styles
                const currentParaStyle = nextEl.getAttribute("style") || "";

                // Build comprehensive style string
                let styleParts = [];
                if (currentParaStyle) {
                  // Parse existing styles and rebuild
                  const existingStyles = currentParaStyle
                    .split(";")
                    .filter((s) => s.trim());
                  existingStyles.forEach((s) => {
                    const trimmed = s.trim();
                    if (
                      !trimmed.includes("clear") &&
                      !trimmed.includes("display") &&
                      !trimmed.includes("width") &&
                      !trimmed.includes("float")
                    ) {
                      styleParts.push(trimmed);
                    }
                  });
                }

                // Add our required styles
                styleParts.push("clear: both !important");
                styleParts.push("display: block !important");
                styleParts.push("width: 100% !important");
                styleParts.push("float: none !important");
                styleParts.push("overflow: hidden !important");

                const newParaStyle = styleParts.join("; ");
                nextEl.setAttribute("style", newParaStyle);

                // Also add a class for CSS targeting
                nextEl.classList.add("equation-after-float");
              }

              // Also ensure equation span has styles
              if (equationSpan) {
                const currentSpanStyle =
                  equationSpan.getAttribute("style") || "";

                let newSpanStyle = currentSpanStyle;
                if (!currentSpanStyle.includes("clear")) {
                  newSpanStyle = `${currentSpanStyle ? currentSpanStyle + "; " : ""
                    }clear: both !important;`;
                } else if (!currentSpanStyle.includes("!important")) {
                  newSpanStyle = currentSpanStyle.replace(
                    /clear:\s*both;?/gi,
                    "clear: both !important;"
                  );
                }
                if (
                  !newSpanStyle.includes("display: block") &&
                  !newSpanStyle.includes("display:block")
                ) {
                  newSpanStyle = `${newSpanStyle} display: block !important;`;
                }
                if (newSpanStyle !== currentSpanStyle) {
                  equationSpan.setAttribute("style", newSpanStyle);
                }
              }

              // Remove PlaygroundEditorTheme__paragraph class from paragraph
              if (nextEl.tagName === "P") {
                nextEl.classList.remove("PlaygroundEditorTheme__paragraph");
              }

              // Break after processing since we've modified the DOM structure
              break;
            }
          }
        }
      });

      // Remove PlaygroundEditorTheme__paragraph class from all paragraphs
      const allParagraphs = container.querySelectorAll("p");
      allParagraphs.forEach((p) => {
        p.classList.remove("PlaygroundEditorTheme__paragraph");
      });

      let result = container.innerHTML;

      // If we processed editor-shell, we need to return the full structure
      if (hasEditorShell && editorShell) {
        // Return the editor-shell with its updated children
        result = editorShell.outerHTML;
      }

      return result;
    } catch (error) {
      return htmlString;
    }
  };

  // Handle editor content changes
  const handleEditorChange = (content) => {
    // Clean content first to remove empty paragraphs and class names
    const cleanedContent = cleanHtmlContent(content);
    // Ensure clearance for elements after floated images
    const contentWithClearance =
      ensureClearanceAfterFloatedImages(cleanedContent);

    // Don't track changes during initial load or initialization phase
    if (!isInitializedRef.current || isInitializingRef.current) {
      // Use silent update during initialization to avoid triggering hasUnsavedChanges
      setFormDataSilent({ content: contentWithClearance });
      // If we have initial content set, update it to match what we just cleaned
      // This ensures consistency if the editor fires multiple change events during init
      if (isInitializedRef.current && initialContentRef.current !== null) {
        initialContentRef.current = contentWithClearance;
      }
      return;
    }

    // Update form data silently first, then check if content actually changed
    setFormDataSilent({ content: contentWithClearance });

    // Only set unsaved changes if we have initial content to compare against
    // and the content is actually different
    if (initialContentRef.current !== null && initialContentRef.current !== undefined) {
      // Normalize both strings for comparison (trim whitespace)
      const normalizedCurrent = (contentWithClearance || "").trim();
      const normalizedInitial = (initialContentRef.current || "").trim();

      if (normalizedInitial !== normalizedCurrent) {
        setHasUnsavedChanges(true);
      } else {
        setHasUnsavedChanges(false);
      }
    } else {
      // If initialContentRef is not set yet, don't mark as changed
      // This can happen if editor fires change before initialization completes
      setHasUnsavedChanges(false);
    }
  };

  // Handle short description editor content changes.
  // Dirty detection for short description is handled via the Lexical undo
  // history (onDirtyChange on the Editor below) so we only update state here.
  const handleShortDescriptionChange = (content) => {
    const cleanedContent = cleanHtmlContent(content);
    const contentWithClearance = ensureClearanceAfterFloatedImages(cleanedContent);
    setFormDataSilent({ shortDescription: contentWithClearance });
  };

  // Track if content has been cleaned to avoid infinite loops
  const contentCleanedRef = useRef(false);

  // Clean content when initially loaded from API
  useEffect(() => {
    if (
      formData.content &&
      !contentCleanedRef.current &&
      !loading &&
      mode === "edit"
    ) {
      // Clean the content when it's first loaded from API
      const cleanedContent = cleanHtmlContent(formData.content);
      const contentWithClearance =
        ensureClearanceAfterFloatedImages(cleanedContent);
      if (contentWithClearance !== formData.content) {
        contentCleanedRef.current = true;
        // Use silent update during initial cleaning to avoid triggering hasUnsavedChanges
        setFormDataSilent({ content: contentWithClearance });
        // Also update initialContentRef if it's already set, to keep them in sync
        if (initialContentRef.current !== null) {
          initialContentRef.current = contentWithClearance;
        }
      } else {
        contentCleanedRef.current = true;
      }
    }
  }, [formData.content, loading, mode, setFormDataSilent]);

  // Initialize the editor and set initial content
  useEffect(() => {
    if (formData.content && !isInitializedRef.current && !loading) {
      // Set initializing flag to prevent change tracking during setup
      isInitializingRef.current = true;

      // Use cleaned content for initial reference (with clearance applied)
      const cleanedContent = cleanHtmlContent(formData.content);
      const contentWithClearance =
        ensureClearanceAfterFloatedImages(cleanedContent);

      // Set initial content reference BEFORE marking as initialized
      // This ensures we have a baseline for comparison
      initialContentRef.current = contentWithClearance;

      // Mark as initialized
      isInitializedRef.current = true;

      // Ensure hasUnsavedChanges is false when initializing
      setHasUnsavedChanges(false);

      // Allow a small delay before allowing change tracking
      // This gives the editor time to fire any initial change events
      setTimeout(() => {
        isInitializingRef.current = false;
        // Final check: if content hasn't changed, ensure hasUnsavedChanges is false
        const currentCleaned = cleanHtmlContent(formData.content);
        const currentWithClearance = ensureClearanceAfterFloatedImages(currentCleaned);
        if (initialContentRef.current === currentWithClearance) {
          setHasUnsavedChanges(false);
        }
      }, 500);
    }
  }, [formData.content, loading, setHasUnsavedChanges]);

  // Reset unsaved changes when content is loaded from API
  useEffect(() => {
    if (loading === false && formData.content && isInitializedRef.current && !isInitializingRef.current) {
      // If this is a fresh load (not from user editing), reset unsaved changes
      // Compare with the cleaned version of current content
      const cleanedContent = cleanHtmlContent(formData.content);
      const contentWithClearance =
        ensureClearanceAfterFloatedImages(cleanedContent);
      const normalizedCurrent = (contentWithClearance || "").trim();
      const normalizedInitial = (initialContentRef.current || "").trim();
      if (normalizedInitial === normalizedCurrent) {
        setHasUnsavedChanges(false);
      }
    }
  }, [loading, formData.content]);


  // Maintain sticky positioning for AppBar and toolbar
  useEffect(() => {
    let rafId = null;
    let checkTimeout = null;
    let isChecking = false;
    let scrollHandler = null;

    const ensureStickyPositioning = () => {
      if (isChecking) return;
      isChecking = true;

      try {
        // Find AppBar element
        const appBar = appBarRef.current || document.querySelector('.appbar');
        if (appBar) {
          appBarRef.current = appBar;
          // Always apply sticky styles to ensure they're maintained
          appBar.style.setProperty('position', 'sticky', 'important');
          appBar.style.setProperty('top', '0', 'important');
          appBar.style.setProperty('z-index', '1000', 'important');

          // Check parent containers for overflow issues that break sticky
          let parent = appBar.parentElement;
          while (parent && parent !== document.body) {
            const parentStyle = window.getComputedStyle(parent);
            // If parent has overflow that breaks sticky, we need to handle it
            if (parentStyle.overflow === 'hidden' ||
              parentStyle.overflow === 'auto' ||
              parentStyle.overflow === 'scroll') {
              // Parent has overflow that may affect sticky - don't modify
            }
            parent = parent.parentElement;
          }
        }

        // Find ALL toolbar elements (Short Description + Content) and apply sticky styles to each
        const toolbars = document.querySelectorAll('.richtext-editor-wrapper .toolbars');
        toolbars.forEach((toolbar) => {
          // Always apply sticky styles - top clears AppBar (padding + title + subheading)
          toolbar.style.setProperty('position', 'sticky', 'important');
          toolbar.style.setProperty('top', '83px', 'important');
          toolbar.style.setProperty('z-index', '998', 'important');
          toolbar.style.setProperty('background', '#f9f9f9', 'important');
          toolbar.style.setProperty('margin', '0', 'important');
          toolbar.style.setProperty('padding', '2px 8px', 'important');
        });
      } catch (error) {
        // Error ensuring sticky positioning
      } finally {
        isChecking = false;
      }
    };

    // Use requestAnimationFrame for smooth checking
    const scheduleCheck = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        ensureStickyPositioning();
      });
    };

    // Debounced version for use in observers
    const debouncedEnsureSticky = () => {
      if (checkTimeout) {
        clearTimeout(checkTimeout);
      }
      checkTimeout = setTimeout(scheduleCheck, 50);
    };

    // Initial check
    const initialCheck = () => {
      ensureStickyPositioning();
      // Check again after a short delay to catch any late DOM changes
      setTimeout(ensureStickyPositioning, 300);
      setTimeout(ensureStickyPositioning, 1000);
    };

    // Set up MutationObserver to watch for style changes
    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;
      mutations.forEach((mutation) => {
        const target = mutation.target;
        if (
          target === appBarRef.current ||
          target === toolbarRef.current ||
          (appBarRef.current && target.contains(appBarRef.current)) ||
          (toolbarRef.current && target.contains(toolbarRef.current)) ||
          (mutation.type === 'attributes' &&
            (mutation.attributeName === 'style' || mutation.attributeName === 'class'))
        ) {
          shouldCheck = true;
        }
      });
      if (shouldCheck) {
        debouncedEnsureSticky();
      }
    });

    // Observe elements
    const observeElements = () => {
      const appBar = appBarRef.current || document.querySelector('.appbar');
      const toolbars = document.querySelectorAll('.richtext-editor-wrapper .toolbars');
      const editorWrapper = toolbarRef.current || document.querySelector('.richtext-editor-wrapper');
      const container = document.querySelector('.richtext-editor-container');

      if (appBar) {
        appBarRef.current = appBar;
        observer.observe(appBar, {
          attributes: true,
          attributeFilter: ['style', 'class'],
        });
        // Also observe parent
        if (appBar.parentElement) {
          observer.observe(appBar.parentElement, {
            attributes: true,
            attributeFilter: ['style', 'class'],
          });
        }
      }
      toolbars.forEach((toolbar) => {
        observer.observe(toolbar, {
          attributes: true,
          attributeFilter: ['style', 'class'],
        });
      });
      if (editorWrapper) {
        observer.observe(editorWrapper, {
          attributes: true,
          attributeFilter: ['style', 'class'],
          childList: true,
        });
      }
      if (container) {
        observer.observe(container, {
          attributes: true,
          attributeFilter: ['style', 'class'],
        });
      }
    };

    // Start observing
    initialCheck();
    setTimeout(observeElements, 100);
    setTimeout(observeElements, 500);

    // Also check on scroll events (sticky can break during scroll)
    scrollHandler = () => {
      scheduleCheck();
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });
    window.addEventListener('resize', scrollHandler, { passive: true });

    // Periodic check as a fallback
    const intervalId = setInterval(ensureStickyPositioning, 2000);

    // Cleanup
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (checkTimeout) {
        clearTimeout(checkTimeout);
      }
      observer.disconnect();
      clearInterval(intervalId);
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
        window.removeEventListener('resize', scrollHandler);
      }
    };
  }, []);

  // Build full URL with basename for window.location (workaround for React Router location sync)
  const buildFullViewChildNodeUrl = (procedureTreeRef) => {
    const pathname = window.location.pathname;
    const basename = pathname.split("/").filter(Boolean)[0];
    const base = basename ? `/${basename}` : "";
    const path = `/view-childnode/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}/${procedureTreeRef}${substep_id_ref ? `?substep_id_ref=${substep_id_ref}` : ""}`;
    return `${window.location.origin}${base}${path}`;
  };

  // Snackbar handlers
  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // AppBar back handler
  const handleAppBarBack = useCallback(() => {
    // Use history.back() then reload so UI matches URL (React Router location sync issue)
    window.history.back();
    // CRITICAL: Only schedule reload when there are NO unsaved changes. Otherwise,
    // when user cancels the confirm dialog, the reload would still fire 50ms later,
    // triggering the browser's beforeunload dialog ("Changes that you made may not be saved.").
    const hasDirtyState = hasUnsavedChanges || isShortDescriptionDirty;
    if (!hasDirtyState && !isLoading) {
      setTimeout(() => {
        window.location.reload();
      }, 50);
    }
  }, [hasUnsavedChanges, isShortDescriptionDirty, isLoading]);

  // Save handlers
  const handleSaveClick = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleConfirmSave = async () => {
    if (!comment) {
      showSnackbar("Comment is mandatory.", "error");
      return;
    }

    setIsLoading(true);
    setShowModal(false);

    try {
      // Clean content before saving to remove empty paragraphs and class names
      const cleanedContent = cleanHtmlContent(formData.content);
      const finalContent = ensureClearanceAfterFloatedImages(cleanedContent);

      const richtextData = {
        short_description: formData.shortDescription,
        revision_id_ref: revisionId,
        section_id_ref: sectionId,
        part_required_id_ref: partId,
        rich_text_content: finalContent,
        rich_text_category_ref:
          formData.categoryId && formData.categoryId.length > 0
            ? formData.categoryId
            : null,
        category_info: {
          category_id: formData.categoryId || "",
          category_name: formData.categoryName || "",
        },
        substep_id_ref: substepId === "null" ? "" : substepId,
        step_id_ref: stepId,
      };

      let response;
      if (mode === "edit") {
        // Edit mode - use updateRichText
        const { signature: _, ...richtextDataWithoutSignature } = richtextData;
        const audit_trail_data = await getAuditTrailData({
          action: "UPDATE_RICH_TEXT",
          comment: comment,
          parent_version: versionId,
          version_content: {
            action_to_show: `Edit ${pageTitle} for ${partnumber}, Rev ${revision}`,
            partNumber: partnumber,
            revision,
            status: currentStatus,
            item_code_type: item_code_type,
            type: "rich-text",
            procedure_revision_id: revisionId,
            tree_node_id: richTextData?.tree_node_id,
            rich_text_node_id: richTextData?.detail?.rich_text_node_id,
            rich_text_tree_node_id:
              richTextData?.detail?.rich_text_tree_node_id,
            element_id:
              richTextData?.detail?.element_id ?? richTextData?.element_id,
            procedure_tree_ref:
              richTextData?.detail?.procedure_tree_ref ??
              richTextData?.procedure_tree_ref,
          },
        });

        richtextData.audit_trail_data = audit_trail_data;
        richtextData.procedure_tree_ref =
          richTextData?.detail?.procedure_tree_ref;
        richtextData.sibling_order = (
          richTextData?.sibling_order ||
          richTextData?.sibling_order?.toString() ||
          "1"
        ).toString();

        response = await dispatch(
          updateRichText({
            data: richtextData,
            id: richTextData?.detail?.rich_text_node_id,
            allNodesData,
            partnumber,
            revision,
          })
        ).unwrap();
        showSnackbar("Rich Text updated successfully!", "success");
        setHasUnsavedChanges(false);
        clearStoredData();
        // Use full page navigation so UI updates (React Router useLocation can stay out of sync with URL)
        window.location.assign(
          buildFullViewChildNodeUrl(richTextData?.detail?.procedure_tree_ref)
        );
      } else {
        // Add mode - use createRichText
        richtextData.sibling_order = (siblingOrder ?? 1).toString();
        const { signature: _, ...richtextDataWithoutSignature } = richtextData;
        const createPayload = {
          richtextData,
          comment,
          version_content: {
            action_to_show: `Save ${pageTitle} for ${partnumber}, Rev ${revision}`,
            partNumber: partnumber,
            revision,
            status: currentStatus,
            item_code_type: item_code_type,
            type: "rich-text",
            procedure_revision_id: revisionId,
          },
          allNodesData,
          partnumber,
          revision,
        };

        response = await dispatch(createRichText(createPayload)).unwrap();

        showSnackbar("Rich Text created successfully!", "success");
        setHasUnsavedChanges(false);
        clearStoredData();
        // Use full page navigation so UI updates (React Router useLocation can stay out of sync with URL)
        window.location.assign(
          buildFullViewChildNodeUrl(response?.results?.procedure_tree_ref)
        );
      }
    } catch (error) {
      showSnackbar(
        `Error ${mode === "add" ? "creating" : "updating"} rich text: ${error?.message
        }`,
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Preview functionality
  const handlePreviewClick = () => {
    // For edit mode, save draft to IndexedDB for persistence
    if (mode === "edit") {
      try {
        saveForPreview();
      } catch (err) {
        // Continue with preview even if save fails
      }
    }
    // Note: For add mode, we don't need to save to localStorage since
    // we're passing data directly via Redux

    // Construct draftChild object for rich text preview
    const draftChild = {
      tree_node_id:
        mode === "edit" ? rich_text_id_ref : `draft-richtext-${Date.now()}`,
      type: "rich-text",
      mode: mode, // "add" or "edit"
      isDraft: true,
      sibling_order: (mode === "add"
        ? siblingOrder ?? 1
        : richTextData?.sibling_order || 1
      ).toString(),
      section_id_ref: section_id_ref,
      step_id_ref: step_id_ref,
      substep_id_ref: substep_id_ref || null,
      detail: {
        short_description: formData.shortDescription,
        rich_text_content: formData.content,
        category_info: {
          category_id: formData.categoryId,
          category_name: formData.categoryName,
          field_name: formData.categoryFieldName,
          description: formData.categoryDescription,
        },
      },
    };

    // Dispatch preview with draft rich text
    dispatch(
      openPreview({
        partId: partId,
        revisionId: revisionId,
        draftChild: draftChild,
      })
    );
  };

  // Lexical editor configuration
  const setInitialHtmlContent = (htmlString) => {
    if (!htmlString) return undefined;

    // Clean the HTML before parsing (using the same cleanHtmlContent function)
    const cleanedHtml = cleanHtmlContent(htmlString);

    // Create editor state from HTML
    const initialEditorState = (editor) => {
      // Parse HTML string into DOM
      const parser = new DOMParser();
      const dom = parser.parseFromString(cleanedHtml, "text/html");

      editor.update(() => {
        // Generate nodes from DOM
        const nodes = $generateNodesFromDOM(editor, dom, {
          preserveWhitespace: true,
        });
        const root = $getRoot();
        root.clear();
        root.append(...nodes);
      });
    };

    return initialEditorState;
  };

  const initialConfig = useMemo(
    () => ({
      editorState: formData.content
        ? setInitialHtmlContent(formData.content)
        : isCollab
          ? null
          : emptyEditor,
      html: { import: buildImportMap() },
      namespace: "Playground",
      nodes: [...PlaygroundNodes],
      onError: () => { },
      theme: PlaygroundEditorTheme,
    }),
    [formData.content, isCollab, emptyEditor]
  );

  // Separate initial config for Short Description field
  const shortDescriptionInitialConfig = useMemo(
    () => ({
      editorState: formData.shortDescription
        ? setInitialHtmlContent(formData.shortDescription)
        : isCollab
          ? null
          : emptyEditor,
      html: { import: buildImportMap() },
      namespace: "ShortDescription",
      nodes: [...PlaygroundNodes],
      onError: () => { },
      theme: PlaygroundEditorTheme,
    }),
    [formData.shortDescription, isCollab, emptyEditor]
  );

  // Set task reference for edit mode
  useEffect(() => {
    if (mode === "edit" && richTextData) {
      setTaskReference(
        dispatch,
        `Rich Text ${partnumber}`,
        richTextData?.detail?.rich_text_node_id,
        richTextData?.tree_node_id,
        richTextData?.ltree_path
      );
    }
  }, [mode, richTextData, dispatch, partnumber]);

  // Show loader until API data is loaded and (in edit mode) form data is ready for the editor
  if (loading) {
    return <Loader text="Loading rich text data..." />;
  }
  if (mode === "edit" && richTextData && !dataLoaded) {
    return <Loader text="Loading rich text data..." />;
  }

  return (
    <ToolbarContext>
      <div>
        <style>{`
          /* Force paragraphs with equations after floated images to appear below */
          .editor-shell p[style*="clear: both"],
          .editor-shell p[style*="clear:both"] {
            clear: both !important;
            display: block !important;
            width: 100% !important;
            float: none !important;
          }
          .editor-shell .equation-clearfix-wrapper {
            clear: both !important;
            display: block !important;
            width: 100% !important;
            float: none !important;
            overflow: hidden !important;
          }
          .editor-shell .equation-clearfix-wrapper p {
            clear: both !important;
            display: block !important;
            width: 100% !important;
            float: none !important;
          }
          /* Force equation spans to be block and clear floats */
          .editor-shell p[style*="clear: both"] [data-lexical-equation],
          .editor-shell p[style*="clear:both"] [data-lexical-equation],
          .editor-shell .equation-clearfix-wrapper [data-lexical-equation] {
            clear: both !important;
            display: block !important;
            float: none !important;
            width: 100% !important;
          }
          /* Clearfix div styling */
          .editor-shell .clearfix {
            clear: both !important;
            display: block !important;
            width: 100% !important;
            height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            float: none !important;
          }
          /* More aggressive: Target any paragraph that comes after a floated image */
          .editor-shell p:has(+ p .inline-editor-image.position-left) + p[data-lexical-equation],
          .editor-shell p:has(.inline-editor-image.position-left) + p,
          .editor-shell p:has(.inline-editor-image.position-left) + div {
            clear: both !important;
            display: block !important;
            width: 100% !important;
            float: none !important;
          }
          /* Target paragraphs that contain equations and come after floated images */
          .editor-shell .inline-editor-image.position-left ~ p:has([data-lexical-equation]),
          .editor-shell .inline-editor-image.position-left ~ div:has([data-lexical-equation]) {
            clear: both !important;
            display: block !important;
            width: 100% !important;
            float: none !important;
          }
          /* Sticky toolbar positioning - toolbar sticks below AppBar with clear gap */
          .richtext-editor-wrapper .toolbars {
            position: sticky !important;
            top: 95px !important;
            z-index: 100 !important;
            background: #f9f9f9 !important;
            margin: 0 !important;
            padding: 2px 8px !important;
            box-shadow: none !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            overflow-x: hidden !important;
            overflow-y: hidden !important;
            flex-wrap: wrap !important;
            min-height: 36px !important;
            height: auto !important;
            align-content: center !important;
            gap: 2px 0 !important;
            box-sizing: border-box !important;
          }
          /* Ensure editor wrapper allows sticky positioning - and set toolbar vars for frozen table row */
          .richtext-editor-wrapper {
            position: relative;
            --lexical-toolbar-top: 95px;
            --lexical-toolbar-height: 36px;
            overflow-x: clip; /* Clip toolbar overflow on right - does not break sticky */
          }
          /* Constrain editor shell so toolbar stays within field boundary - clip overflow */
          .richtext-editor-wrapper .editor-shell {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
            overflow-x: clip !important;
          }
          /* Force AppBar to remain sticky - highest specificity */
          header.appbar,
          header.appbar.appbar,
          .appbar,
          div .appbar,
          body .appbar,
          #root .appbar {
            position: sticky !important;
            top: 0 !important;
            z-index: 1000 !important;
            width: 100% !important;
          }
          /* Prevent parent containers from breaking sticky positioning */
          .richtext-editor-container,
          .richtext-description-field {
            /* Don't set overflow here - it breaks sticky */
            position: relative !important;
            overflow: visible !important;
          }
          /* Wrapper: allow overflow-x: clip to contain toolbar; overflow-y: visible for sticky */
          .richtext-editor-wrapper {
            overflow-x: clip !important;
            overflow-y: visible !important;
          }
          /* Ensure toolbar sticky positioning is maintained - multiple selectors for specificity */
          .richtext-editor-wrapper .toolbars,
          .richtext-editor-container .richtext-editor-wrapper .toolbars,
          div .richtext-editor-wrapper .toolbars,
          .toolbars,
          body .toolbars {
            position: sticky !important;
            top: 95px !important;
            z-index: 100 !important;
            background: #f9f9f9 !important;
            margin: 0 !important;
            padding: 2px 8px !important;
            display: flex !important;
            flex-wrap: wrap !important;
            box-shadow: none !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            overflow-x: hidden !important;
            overflow-y: hidden !important;
            min-height: 36px !important;
            height: auto !important;
            align-content: center !important;
            gap: 2px 0 !important;
            box-sizing: border-box !important;
          }
          /* Responsive toolbar: smaller icons and padding so toolbar fits without scrollbar */
          @media (max-width: 1400px) {
            .richtext-editor-wrapper .toolbars .toolbar-item,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item {
              padding: 4px 6px !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item i.format,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item i.format {
              height: 14px !important;
              width: 14px !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item .icon,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item .icon {
              width: 16px !important;
              height: 16px !important;
              margin-right: 4px !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item .text,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item .text {
              font-size: 12px !important;
              height: 18px !important;
              line-height: 18px !important;
              padding-right: 6px !important;
            }
            .richtext-editor-wrapper .toolbars i.chevron-down,
            .richtext-editor-wrapper .toolbars .toolbar-item i.chevron-down,
            .richtext-editor-container .richtext-editor-wrapper .toolbars i.chevron-down,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item i.chevron-down {
              width: 12px !important;
              height: 12px !important;
            }
            .richtext-editor-wrapper .toolbars .divider,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .divider {
              min-height: 24px !important;
              margin: 0 4px !important;
            }
            .richtext-editor-wrapper .toolbars .block-controls .dropdown-button-text,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .block-controls .dropdown-button-text {
              max-width: 5em !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item.font-family .text,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item.font-family .text {
              max-width: 32px !important;
            }
            .richtext-editor-wrapper .toolbars .code-language,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .code-language {
              width: 120px !important;
            }
          }
          @media (max-width: 1200px) {
            .richtext-editor-wrapper .toolbars,
            .richtext-editor-container .richtext-editor-wrapper .toolbars {
              padding: 2px 4px !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item {
              padding: 2px 4px !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item i.format,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item i.format {
              height: 12px !important;
              width: 12px !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item .icon,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item .icon {
              width: 14px !important;
              height: 14px !important;
              margin-right: 2px !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item .text,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item .text {
              font-size: 11px !important;
              height: 16px !important;
              line-height: 16px !important;
              padding-right: 4px !important;
            }
            .richtext-editor-wrapper .toolbars i.chevron-down,
            .richtext-editor-wrapper .toolbars .toolbar-item i.chevron-down,
            .richtext-editor-container .richtext-editor-wrapper .toolbars i.chevron-down,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item i.chevron-down {
              width: 10px !important;
              height: 10px !important;
            }
            .richtext-editor-wrapper .toolbars .divider,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .divider {
              min-height: 20px !important;
              margin: 0 2px !important;
            }
            .richtext-editor-wrapper .toolbars .block-controls .dropdown-button-text,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .block-controls .dropdown-button-text {
              max-width: 4em !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item.font-family .text,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item.font-family .text {
              max-width: 28px !important;
            }
            .richtext-editor-wrapper .toolbars .code-language,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .code-language {
              width: 100px !important;
            }
          }
          @media (max-width: 992px) {
            .richtext-editor-wrapper .toolbars,
            .richtext-editor-container .richtext-editor-wrapper .toolbars {
              min-height: 32px !important;
              padding: 2px 4px !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item {
              padding: 2px 3px !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item i.format,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item i.format {
              height: 11px !important;
              width: 11px !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item .icon,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item .icon {
              width: 12px !important;
              height: 12px !important;
              margin-right: 2px !important;
            }
            .richtext-editor-wrapper .toolbars .toolbar-item .text,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .toolbar-item .text {
              font-size: 10px !important;
              height: 14px !important;
              line-height: 14px !important;
              padding-right: 2px !important;
            }
            .richtext-editor-wrapper .toolbars .divider,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .divider {
              min-height: 18px !important;
              margin: 0 2px !important;
            }
            .richtext-editor-wrapper .toolbars .block-controls .dropdown-button-text,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .block-controls .dropdown-button-text {
              max-width: 3.5em !important;
            }
            .richtext-editor-wrapper .toolbars .code-language,
            .richtext-editor-container .richtext-editor-wrapper .toolbars .code-language {
              width: 85px !important;
            }
          }
          /* Make sure #root doesn't break sticky - but don't force overflow changes */
          #root {
            position: relative !important;
          }
        `}</style>
        <AppBar
          onBack={handleAppBarBack}
          subTitleAboveTitle={
            <>
              {localStorage.getItem("user_name")} is{" "}
              {mode === "add" ? "Creating" : "Editing"}
            </>
          }
          title={pageTitle}
          showTablesDropdown={true}
          showProfileButton={fromVersionHistory ? false : true}
          buttons={
            fromVersionHistory
              ? []
              : [
                { label: "Preview", onClick: handlePreviewClick },
                {
                  label: "Save",
                  onClick: canEdit ? handleSaveClick : undefined,
                  disabled: !canEdit,
                  title: !canEdit
                    ? "You do not have permission to save rich text."
                    : undefined,
                },
              ]
          }
          subheading={breadcrumbText}
          showBlackTitle={true}
          showColorTitle={false}
          subheadingAfterTitle={mode === "edit" ? lastSavedText : undefined}
          versionHistoryHandler={
            mode === "edit" ? () => setShowVersionHistory(true) : null
          }
        />

        <div className="p-6">
          <div className="form-row1">
            <TextField
              label={mode === "edit" ? "Version ID" : "ID (Read Only)"}
              value={versionId}
              readOnly
              width="100%"
              inputStyle={{ color: "#999999" }}
            />
            <div
              className=""
              style={{ width: "17%", marginLeft: "12px", marginTop: "12px" }}
            >
              <InputWithPen
                label="Rich Text Category"
                required={false}
                val={formData.categoryName || ""}
                onClickOnPencil={() => setCategoryModalOpen(true)}
                onClear={() => {
                  setFormData({ categoryId: "", categoryName: "" });
                  setHasUnsavedChanges(true);
                }}
              />
            </div>
            <div
              className=""
              style={{ width: "48%", marginLeft: "20px" }}
            ></div>
          </div>

          <div 
            className="richtext-description-field"
            style={{
              
            }}
          >
            <div
            className="richtext-editor-container"
            style={{ paddingLeft: "10px" }}
          >
            <label className="richtext-editor-label">Short Description</label>
            <div className="richtext-editor-wrapper" ref={toolbarRef}>
              <Suspense>
                <LexicalComposer initialConfig={shortDescriptionInitialConfig}>
                  <SharedHistoryContext>
                    <TableContext>
                      <ToolbarContext>
                        <div className="editor-shell">
                          <Editor
                            handleEditorChange={handleShortDescriptionChange}
                            initialContent={formData.shortDescription || ""}
                            onDirtyChange={handleShortDescriptionDirtyChange}
                          />
                        </div>
                      </ToolbarContext>
                    </TableContext>
                  </SharedHistoryContext>
                </LexicalComposer>
              </Suspense>
            </div>
          </div>
          </div>

          <div
            className="richtext-editor-container"
            style={{ paddingLeft: "10px",marginTop:"-50px" }}
          >
            <label className="richtext-editor-label">Content</label>
            <div className="richtext-editor-wrapper" ref={toolbarRef}>
              <Suspense>
                <LexicalComposer initialConfig={initialConfig}>
                  <SharedHistoryContext>
                    <TableContext>
                      <ToolbarContext>
                        <div className="editor-shell">
                          <Editor
                            handleEditorChange={handleEditorChange}
                            initialContent={formData.content || ""}
                          />
                        </div>
                      </ToolbarContext>
                    </TableContext>
                  </SharedHistoryContext>
                </LexicalComposer>
              </Suspense>
            </div>
          </div>
        </div>

        <SaveModal
          isOpen={showModal}
          onClose={handleCloseModal}
          onSave={handleConfirmSave}
          title={`Save ${pageTitle}`}
          subtitle={breadcrumbText}
          editorName={localStorage.getItem("user_name") || "Unknown User"}
          comment={comment}
          onCommentChange={(e) => setComment(e.target.value)}
          isLoading={isLoading}
          showLoader={true}
        />

        {isLoading && (
          <Loader
            text={
              mode === "add" ? "Saving rich text..." : "Updating rich text..."
            }
          />
        )}

        {snackbar.open && (
          <Snackbar
            message={snackbar.message}
            type={snackbar.type}
            onClose={handleCloseSnackbar}
          />
        )}

        {showVersionHistory && (
          <VersionHistoryPopup
            onClose={() => setShowVersionHistory(false)}
            procedurePartNumber={partnumber}
            procedureRevision={revision}
            versionHistories={versionHistories}
            mode={mode}
            versionId={versionId}
            nodeId={richTextData?.tree_node_id || rich_text_id_ref || ""}
            nodeIdField="rich_text_node_id"
            breadcrumbText={breadcrumbText}
            revisionTreeNodes={allNodesData}
            nodeHistoryMatchIds={
              Array.isArray(richTextVersionHistoryMatchIds) &&
              richTextVersionHistoryMatchIds.length > 0
                ? richTextVersionHistoryMatchIds
                : undefined
            }
            nodeHistoryLeafMediaOrdinal={
              richTextNodeHistoryLeafOrdinal != null &&
              Number.isFinite(Number(richTextNodeHistoryLeafOrdinal))
                ? Number(richTextNodeHistoryLeafOrdinal)
                : undefined
            }
            nodeHistoryLeafMediaPhraseRegex="Rich(?:\\s*Text)?"
            procedureRevisionIdForNodeHistory={revisionId}
            restrictNodeHistoryToProcedureRevision={
              allVersionHistories.length === 0
            }
            emptyMessage={
              mode === "add"
                ? "No version history available for new rich texts"
                : "No version history available"
            }
            initialFilterType="versions_for_this_page"
            onViewDetails={(row) => {
              const aid = row.audit_trail_id || row.audit_trail_id_ref;
              if (!aid) return;
              const path = getVersionHistoryAuditPreviewPath(
                aid,
                row.action || row.action_to_show
              );
              if (!path) return;
              setShowVersionHistory(false);
              navigate(path);
            }}
            isRowViewActionDisabled={(row) =>
              !(row.audit_trail_id || row.audit_trail_id_ref)
            }
            exportCsvDisabled={versionHistoryLoading}
          />
        )}
        {categoryModalOpen && (
          <CategoryPopup
            handleSelectCategory={(category) => {
              setFormData({
                categoryId: category.category_id,
                categoryName: category.category_name,
              });
              setHasUnsavedChanges(true);
              setCategoryModalOpen(false);
            }}
            onClose={() => setCategoryModalOpen(false)}
            disableFilter={true}
            cat_type={"Rich Text > Rich Text Category"}
          />
        )}
      </div>
    </ToolbarContext>
  );
};

export default RichTextForm;
